import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useAuth } from './use-auth';

// Keep the serialized context bounded — it is sent to the LLM on every advice
// turn and counts toward cost.
const MAX_CONTEXT_CHARS = 6000;

export interface ScreenContext {
  // Short human label for the UI hint (e.g. "Review 2 · Economic model").
  label: string;
  // Text block fed to the RAG "advice" mode. Empty when there's nothing useful.
  text: string;
}

const REVIEW_NAMES: Record<string, string> = {
  'review-1': 'Review 1 — Opportunity & market',
  'review-2': 'Review 2 — Business model',
  'review-3': 'Review 3 — Risk factors & stakeholders',
  'review-4': 'Review 4 — Action plan'
};

// Turn a sub-route slug ("economic-model") or a persisted parent_type segment
// ("economic_model") into a readable label ("Economic model").
const humanizeSlug = (slug: string): string => {
  const s = slug.replace(/[-_]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

// Human label for a document's persisted `parent_type`
// (e.g. "review_2_economic_model" -> "Review 2 · Economic model"), reusing the
// same review names + slug humanizer as the on-screen context hint. Falls back to
// a humanized slug for non-review screens, and to '' when there's nothing to show.
export const screenLabelFromParentType = (parentType?: string | null): string => {
  if (!parentType) {
    return '';
  }
  const match = parentType.match(/^review_([1-4])_(.+)$/);
  if (match) {
    const reviewName = (REVIEW_NAMES[`review-${match[1]}`] || `Review ${match[1]}`).split(' — ')[0];
    const sub = humanizeSlug(match[2]);
    return sub ? `${reviewName} · ${sub}` : reviewName;
  }
  return humanizeSlug(parentType);
};

const safeStringify = (value: unknown): string => {
  try {
    const json = JSON.stringify(value, null, 2);
    if (!json) {
      return '';
    }
    return json.length > MAX_CONTEXT_CHARS
      ? json.slice(0, MAX_CONTEXT_CHARS) + '\n…(truncated)'
      : json;
  } catch {
    return '';
  }
};

/**
 * Captures the context of the screen the user is currently on (which IpOp review
 * + the data entered there + the active project), for the assistant's "advice"
 * mode. Reads route + Redux + auth, so it must be called inside the router and
 * the Redux/auth providers (e.g. from the dashboard layout).
 */
export const useScreenContext = (): ScreenContext => {
  const location = useLocation();
  const { activeProject } = useAuth();

  const review1 = useSelector((state: RootState) => state.review1);
  const review2 = useSelector((state: RootState) => (state as any).review2?.review2);
  const review3 = useSelector((state: RootState) => (state as any).review3?.review3);
  const review4 = useSelector((state: RootState) => (state as any).review4?.review4);

  const path = location.pathname;
  const match = path.match(/\/(review-[1-4])(?:\/([^/?]+))?/);

  const projectLine = activeProject?.name
    ? `Project: ${activeProject.name}${
        activeProject.description ? ` — ${activeProject.description}` : ''
      }`
    : '';

  if (!match) {
    // Outside a review (dashboard, quick-actions, settings…): only the route +
    // project are useful as context.
    const label = 'Current screen';
    const lines = [`Current page: ${path}`, projectLine].filter(Boolean);
    return { label, text: lines.join('\n') };
  }

  const reviewKey = match[1]; // "review-2"
  const subSlug = match[2] || '';
  const reviewName = REVIEW_NAMES[reviewKey] || reviewKey;
  const subLabel = humanizeSlug(subSlug);
  const label = subLabel
    ? `${reviewName.split(' — ')[0]} · ${subLabel}`
    : reviewName.split(' — ')[0];

  let data: unknown = null;
  if (reviewKey === 'review-1') {
    data = {
      activeCustomer: review1?.activeCustomer,
      activeCompany: review1?.activeCompany,
      customers: review1?.customers?.data,
      companies: review1?.companies?.data,
      solution: review1?.solution
    };
  } else if (reviewKey === 'review-2') {
    data = review2;
  } else if (reviewKey === 'review-3') {
    data = review3;
  } else if (reviewKey === 'review-4') {
    data = review4;
  }

  const lines = [
    `Current screen: ${reviewName}${subLabel ? ` > ${subLabel}` : ''}`,
    projectLine,
    '',
    'Data entered on this screen (JSON):',
    safeStringify(data)
  ].filter((l) => l !== undefined);

  return { label, text: lines.join('\n').trim() };
};
